import { Blac, Cubit } from "blac-next";
import { KnownProgram } from "src/constants/programs";
import TaskManagementBloc, {
  type TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import reportErrorSentry from "src/lib/reportErrorSentry";
import {
  APPOINTMENT_RATING_GROUP,
  getAppointmentRatingConfig,
  type AppointmentRatingConfig
} from "src/state/AppointmentRatingCubit/appointmentRatingConfig";

export interface AppointmentRatingState {
  task?: TaskResponseKnown;
  hiddenFields: Record<string, string>;
  questionnaireId?: AppointmentRatingConfig["questionnaireId"];
  isLoading: boolean;
}

export default class AppointmentRatingCubit extends Cubit<AppointmentRatingState> {
  constructor() {
    super({
      task: undefined,
      hiddenFields: {},
      questionnaireId: undefined,
      isLoading: false
    });
  }

  loadAppointmentRatingTask = async (appointmentId: string): Promise<void> => {
    if (!appointmentId) {
      return;
    }

    this.emit({
      task: undefined,
      hiddenFields: {},
      questionnaireId: undefined,
      isLoading: true
    });

    try {
      const taskManagementState = Blac.getBloc(TaskManagementBloc);
      const tasks = await taskManagementState.loadProgramTasks(
        KnownProgram.CARE
      );

      const task = tasks
        ?.filter((t) => t.group === APPOINTMENT_RATING_GROUP)
        .find((t) => t.additionalData?.appointmentId === appointmentId);

      if (task) {
        const config = getAppointmentRatingConfig(task.slug);
        const providerName =
          (task.additionalData?.[config.nameDataKey] as string) ?? "";
        const providerUserId =
          (task.additionalData?.[config.userIdDataKey] as string) ?? "";
        const taskAppointmentId = task.additionalData?.appointmentId as string;

        this.emit({
          task,
          questionnaireId: config.questionnaireId,
          hiddenFields: {
            taskId: task.id ?? "",
            [config.nameHiddenField]: providerName,
            [config.userIdHiddenField]: providerUserId,
            appointmentId: taskAppointmentId
          },
          isLoading: false
        });
      } else {
        this.emit({
          task: undefined,
          hiddenFields: {},
          questionnaireId: undefined,
          isLoading: false
        });
      }
    } catch (error) {
      reportErrorSentry("Failed to load appointment rating task", {
        error
      });

      this.emit({
        task: undefined,
        hiddenFields: {},
        questionnaireId: undefined,
        isLoading: false
      });
    }
  };
}
